import React, { useEffect, useState, useRef } from "react";
import IncomeEntryModal from "./IncomeEntryModal";
import ReceiptDownload from "./ReceiptDownload";
import "../styles/Income.css";
import API_URL from "../apiConfig";

/*
  Diagnostic ManageIncome component
  - Shows debug information to help find why the page appears blank.
  - Displays fetch status, raw response text and parsed JSON preview.
  - Avoids throwing if IncomeEntryModal import fails.
  - Use this to quickly identify whether the API is returning data or failing silently.
*/

const DEFAULT_INCOME_HEADS = [
  "Tuition Fee",
  "Scholarship",
  "Donation",
  "Hostel Fee",
  "Transport Fee",
  "Trustee Funds",
  "Other"
];

function loadHeadsFromStorage() {
  try {
    const raw = localStorage.getItem("income_heads");
    const saved = raw ? JSON.parse(raw) : [];
    return Array.from(new Set([...(Array.isArray(saved) ? saved : []), ...DEFAULT_INCOME_HEADS]));
  } catch {
    return DEFAULT_INCOME_HEADS.slice();
  }
}

function safeNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}
function formatDateShort(val) {
  if (!val) return "";
  try {
    if (typeof val === "string" && val.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(val)) return val.slice(0, 10);
    const d = new Date(val);
    if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    return String(val).slice(0, 10);
  } catch {
    return String(val || "");
  }
}
function formatAmount(v) {
  const n = safeNumber(v);
  return "₹" + n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function ManageIncome({ token, user }) {
  const [incomes, setIncomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [heads, setHeads] = useState(loadHeadsFromStorage());
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editIncome, setEditIncome] = useState(null);
  const [pdfLoadingId, setPdfLoadingId] = useState(null);

  // Diagnostic state:
  const [lastFetchStatus, setLastFetchStatus] = useState(null);
  const [lastFetchText, setLastFetchText] = useState("");
  const [lastFetchJsonPreview, setLastFetchJsonPreview] = useState("");
  const [lastFetchTime, setLastFetchTime] = useState(null);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    fetchIncomes().catch(() => {});
    return () => { mountedRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function fetchIncomes() {
    setLoading(true);
    setFetchError("");
    setLastFetchStatus(null);
    setLastFetchText("");
    setLastFetchJsonPreview("");
    setLastFetchTime(new Date().toISOString());
    if (!token) {
      const msg = "No token provided — ManageIncome requires a valid token prop.";
      console.error(msg);
      setFetchError(msg);
      setLoading(false);
      return;
    }

    let res;
    try {
      res = await fetch(`${API_URL}/api/income_expense/income`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: "no-cache"
      });
    } catch (err) {
      console.error("Network error fetching incomes:", err);
      setFetchError("Network error fetching incomes. Check backend availability and CORS. See console for details.");
      setLoading(false);
      return;
    }

    setLastFetchStatus(`${res.status} ${res.statusText || ""}`);

    let text = "";
    try {
      text = await res.text();
    } catch (err) {
      text = `<unable to read response text: ${String(err)}>`;
    }
    setLastFetchText(text.slice(0, 5000)); // keep preview length reasonable

    // Try parse JSON (if any)
    try {
      const json = JSON.parse(text || "null");
      setLastFetchJsonPreview(JSON.stringify(Array.isArray(json) ? json.slice(0, 20) : json, null, 2).slice(0, 5000));
      if (!mountedRef.current) return;
      setIncomes(Array.isArray(json) ? json : []);
      if (!Array.isArray(json)) {
        // if API returned object, show a message
        setFetchError("API returned non-array JSON. See debug panel below.");
      }
    } catch (err) {
      // not JSON
      console.warn("Response is not JSON:", err);
      // If status is OK but not JSON, show the text
      if (res.ok) {
        setFetchError("API returned non-JSON response. See Raw Response below.");
        setIncomes([]);
      } else {
        setFetchError(`Server returned ${res.status}. See Raw Response below.`);
        setIncomes([]);
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }

  function reloadHeads() {
    setHeads(loadHeadsFromStorage());
  }

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this income record?")) return;
    try {
      const res = await fetch(`${API_URL}/api/income_expense/income/${encodeURIComponent(id)}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`Delete failed (${res.status}) ${txt}`);
      }
      await fetchIncomes();
    } catch (err) {
      console.error("ManageIncome.handleDelete error:", err);
      alert("Failed to delete income. See console for details.");
    }
  };

  const handleAddHead = (newHead) => {
    if (!newHead || !newHead.trim()) return;
    const trimmed = newHead.trim();
    try {
      const raw = localStorage.getItem("income_heads");
      const saved = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(saved)) saved.length = 0;
      if (!saved.includes(trimmed)) {
        saved.push(trimmed);
        localStorage.setItem("income_heads", JSON.stringify(saved));
      }
    } catch {
      localStorage.setItem("income_heads", JSON.stringify([trimmed]));
    }
    reloadHeads();
  };

  const filtered = incomes.filter(i => {
    const t = searchTerm.trim().toLowerCase();
    if (!t) return true;
    return (
      (i.receipt_no && String(i.receipt_no).toLowerCase().includes(t)) ||
      (i.account_head && String(i.account_head).toLowerCase().includes(t)) ||
      (i.particulars && String(i.particulars).toLowerCase().includes(t)) ||
      (i.reference && String(i.reference).toLowerCase().includes(t)) ||
      (i.student_name && String(i.student_name).toLowerCase().includes(t)) ||
      (String(i.amount ?? i.amount_paid ?? "").toLowerCase().includes(t))
    );
  });

  const openPDF = async (row) => {
    try {
      setPdfLoadingId(row.id ?? row.fee_id ?? row.receipt_no ?? "pdf");
      await ReceiptDownload.generatePDF(row, {
        student: {
          name: row.student_name,
          batch: row.batch_name || row.batch_id,
          course_year: row.course_year
        }
      });
    } catch (err) {
      console.error("Receipt PDF error:", err);
      alert("Failed to generate PDF. See console for details.");
    } finally {
      setPdfLoadingId(null);
    }
  };

  // Render UI with debug panel so page is never blank
  return (
    <div style={{ padding: 12 }}>
      <h2>Manage Income (diagnostic)</h2>

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
        <button onClick={() => { setEditIncome(null); setShowModal(true); }}>+ Add Income</button>
        <button onClick={() => fetchIncomes()}>Refresh</button>

        <input
          type="text"
          placeholder="Search receipt/head/particulars/student/amount"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{ marginLeft: "auto", padding: "6px 10px", minWidth: 300 }}
        />
      </div>

      {showModal ? (
        typeof IncomeEntryModal === "function" ? (
          <IncomeEntryModal
            token={token}
            income={editIncome}
            editMode={!!editIncome}
            onClose={() => { setShowModal(false); setEditIncome(null); }}
            onSave={() => { setShowModal(false); setEditIncome(null); fetchIncomes(); }}
            user={user}
            heads={heads}
            onAddHead={handleAddHead}
          />
        ) : (
          <div style={{ color: "red", marginBottom: 12 }}>
            IncomeEntryModal component not available or failed to import. Check src/components/IncomeEntryModal.jsx
          </div>
        )
      ) : null}

      <div style={{ marginBottom: 12 }}>
        <strong>Token present:</strong> {token ? "yes" : "NO"} &nbsp;&nbsp;
        <strong>Fetched at:</strong> {lastFetchTime ?? "n/a"} &nbsp;&nbsp;
        <strong>Last status:</strong> {lastFetchStatus ?? "n/a"}
      </div>

      <div style={{ display: "flex", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ border: "1px solid #ddd", padding: 8, borderRadius: 4, marginBottom: 8 }}>
            <strong>Incomes ({incomes.length})</strong>
            {loading && <div>Loading...</div>}
            {fetchError && <div style={{ color: "red" }}>{fetchError}</div>}
          </div>

          <div style={{ maxHeight: 420, overflow: "auto", border: "1px solid #eee", padding: 6 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f3f3f3" }}>
                  <th style={{ textAlign: "left", padding: 6 }}>Receipt</th>
                  <th style={{ textAlign: "left", padding: 6 }}>Date</th>
                  <th style={{ textAlign: "left", padding: 6 }}>Head</th>
                  <th style={{ textAlign: "left", padding: 6 }}>Particulars</th>
                  <th style={{ textAlign: "left", padding: 6 }}>Reference</th>
                  <th style={{ textAlign: "right", padding: 6 }}>Amount</th>
                  <th style={{ textAlign: "left", padding: 6 }}>Mode</th>
                  <th style={{ textAlign: "center", padding: 6 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={8} style={{ padding: 12 }}>No records</td></tr>
                ) : filtered.map((inc) => {
                  const key = inc.id ?? inc.fee_id ?? inc.receipt_no ?? JSON.stringify(inc);
                  return (
                    <tr key={key}>
                      <td style={{ padding: 6 }}>{inc.receipt_no ?? "-"}</td>
                      <td style={{ padding: 6 }}>{formatDateShort(inc.payment_date ?? inc.date ?? inc.created_at)}</td>
                      <td style={{ padding: 6 }}>{inc.account_head ?? "-"}</td>
                      <td style={{ padding: 6 }}>{inc.particulars ?? "-"}</td>
                      <td style={{ padding: 6 }}>{inc.reference ?? "-"}</td>
                      <td style={{ padding: 6, textAlign: "right" }}>{formatAmount(inc.amount ?? inc.amount_paid ?? 0)}</td>
                      <td style={{ padding: 6 }}>{inc.mode ?? "-"}</td>
                      <td style={{ padding: 6, textAlign: "center" }}>
                        <button onClick={() => { setEditIncome(inc); setShowModal(true); }}>Edit</button>
                        <button onClick={() => handleDelete(inc.id ?? inc.fee_id)} style={{ marginLeft: 6 }}>Delete</button>
                        <button onClick={() => openPDF(inc)} style={{ marginLeft: 6 }} disabled={pdfLoadingId !== null}>
                          {pdfLoadingId && (pdfLoadingId === (inc.id ?? inc.fee_id ?? inc.receipt_no)) ? "Generating..." : "PDF"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ width: 440 }}>
          <div style={{ border: "1px solid #ddd", padding: 8, borderRadius: 4, marginBottom: 8 }}>
            <strong>Fetch diagnostics</strong>
          </div>
          <div style={{ fontFamily: "monospace", fontSize: 12, whiteSpace: "pre-wrap", background: "#fff", border: "1px solid #eee", padding: 8, maxHeight: 420, overflow: "auto" }}>
            <div><strong>LastFetchStatus:</strong> {lastFetchStatus ?? "(none)"}</div>
            <div style={{ marginTop: 8 }}><strong>LastFetchTime:</strong> {lastFetchTime ?? "(none)"}</div>
            <div style={{ marginTop: 8 }}><strong>Raw response (first 5k chars):</strong></div>
            <div style={{ borderTop: "1px dashed #ddd", paddingTop: 6 }}>{lastFetchText || "(empty)"}</div>
            <div style={{ marginTop: 8 }}><strong>JSON preview:</strong></div>
            <div style={{ borderTop: "1px dashed #ddd", paddingTop: 6 }}>{lastFetchJsonPreview || "(none)"}</div>
          </div>
        </div>
      </div>
    </div>
  );
}